// create a context for the registration setup

import { Registration } from '@sportninja/common/types'
import { RegistrationTypes, Tax } from '@sportninja/common/types/Registration'
import dayjs from 'dayjs'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { useWaivers, Waiver } from '@sportninja/common/hooks/useWaivers'
import { useAccount, AccountData } from '@sportninja/common/hooks/useAccount'
import {
  CustomForm,
  useCustomForm,
} from '@sportninja/common/hooks/useCustomForm'

type RegistrationSetupState = {
  loading: boolean
  competitionDateRange: {
    starts_at: string | null
    ends_at: string | null
  }
  goLive: boolean
  registrationType: 'player' | 'team'
  paidBy: 'player' | 'team'
  bccEmails: string[]
  currency: string
  taxes: string[]
  customBackgroundImage: string
  customBackgroundImageChanged: boolean
  darkMode: boolean
  waivers: string[]
  registrationForm: {
    [key: string]: boolean
  }
  fieldErrors: string[]
  customError: any
  payment_type: string
  autoApproveRegistrations: boolean
  enableTeamRegistration: boolean
  enablePlayerRegistration: boolean
  payment_provider: string | null
  show_waiver: boolean
  addPlayersOutsideOfRegistration: boolean
  player_form: CustomForm | null
  team_form: CustomForm | null
  headerContent: string
}

type RegistrationSetupContextType = {
  state: RegistrationSetupState
  setState: React.Dispatch<React.SetStateAction<RegistrationSetupState>>
  registrationTypes: RegistrationTypes
  availableTaxes: Tax[]
  submitRegistration: () => Promise<void>
  isUpdated: boolean
  apiWaivers: Waiver[]
  refetchWaivers: () => void
  apiAccount: AccountData | null
  registrationId: string | null
  registration: Registration | null
  setCustomErroByField: (field: string, message: string) => void
  clearCustomErrors: () => void
  createPlayerForm: () => Promise<CustomForm | null>
  createTeamForm: () => Promise<CustomForm | null>
  scheduleId: string
  activeTab: string
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
}

const RegistrationSetupContext = createContext<
  RegistrationSetupContextType | undefined
>(undefined)

export const useRegistrationSetupContext = () => {
  const context = useContext(RegistrationSetupContext)
  if (!context) {
    throw new Error(
      'useRegistrationSetupContext must be used within a RegistrationSetupContextWrapper'
    )
  }
  return context
}

type RegistrationSetupProps = {
  registration: Registration | null
  registrationTypes: RegistrationTypes
  availableTaxes: Tax[]
  scheduleId: string
  accountId: string
  isRegistrationFormOpen: boolean
}

type RegistrationSetupContextWrapperProps = RegistrationSetupProps & {
  children: React.ReactNode
}

const RegistrationSetupContextWrapper = ({
  children,
  registration,
  registrationTypes,
  availableTaxes,
  scheduleId,
  accountId,
  isRegistrationFormOpen = false,
}: RegistrationSetupContextWrapperProps) => {
  const [state, setState] = useState<RegistrationSetupState>({
    show_waiver: false,
    competitionDateRange: {
      starts_at: dayjs().utc().format('YYYY-MM-DD'),
      ends_at: dayjs().utc().format('YYYY-MM-DD'),
    },
    goLive: false,
    registrationType: 'team',
    paidBy: 'player',
    bccEmails: [],
    currency: '2',
    taxes: [],
    customBackgroundImage: '',
    customBackgroundImageChanged: false,
    darkMode: false,
    waivers: [],
    registrationForm: {},
    fieldErrors: [],
    customError: '',
    loading: false,
    payment_type: '1',
    autoApproveRegistrations: false,
    enableTeamRegistration: false,
    enablePlayerRegistration: false,
    addPlayersOutsideOfRegistration: false,
    payment_provider: null,
    player_form: null,
    team_form: null,
    headerContent: '',
  })

  const [activeTab, setActiveTab] = useState('0')

  const { data: apiWaivers, refetch: refetchWaivers } = useWaivers(accountId)

  const { data: apiAccount, refetch: refetchAccount } = useAccount(accountId)

  const { createForm } = useCustomForm()
  const alreadyCreatedPlayerForm = useRef(false)
  const alreadyCreatedTeamForm = useRef(false)

  useEffect(() => {
    if (isRegistrationFormOpen) {
      refetchAccount()
    }
  }, [isRegistrationFormOpen, refetchAccount])

  const isUpdated = useMemo(
    () => Boolean(registration && registration.id),
    [registration]
  )

  const {
    createRegistration,
    updateRegistration,
    uploadRegistrationImage,
    deleteRegistrationImage,
  } = useRegistrationData('')

  // Helper function to update registration with form IDs only
  const updateRegistrationWithFormId = useCallback(
    async (formType: 'player' | 'team', formId: string) => {
      console.log(
        `🔄 updateRegistrationWithFormId: Starting ${formType} form registration update`,
        {
          formType,
          formId,
          isUpdated,
          scheduleId,
          currentPlayerFormId: state?.player_form?.id,
          currentTeamFormId: state?.team_form?.id,
        }
      )

      if (!isUpdated) {
        console.log(
          `⚠️ updateRegistrationWithFormId: Registration not updated yet (isUpdated: ${isUpdated}), skipping form ID update`
        )
        return // Only update if registration already exists
      }

      try {
        const updatePayload = {
          scheduleId: scheduleId,
          allowPlayerRegistration: state.enablePlayerRegistration,
          registrationType:
            state.enableTeamRegistration && !state.enablePlayerRegistration
              ? 'team'
              : 'player',
          enableRegistration: true,
          eventNumber: '',
          currency: Number(state.currency),
          costPerRegistration: 0,
          paymentCurrency: Number(state.currency),
          paymentMethod: Number(state.payment_type),
          goLive: state.goLive,
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id:
            formType === 'player' ? formId : state?.player_form?.id || null,
          team_form_id:
            formType === 'team' ? formId : state?.team_form?.id || null,
          header_content: state.headerContent || null,
        }

        console.log(
          '🔄 updateRegistrationWithFormId: Calling updateRegistration with payload',
          {
            formType,
            player_form_id: updatePayload.player_form_id,
            team_form_id: updatePayload.team_form_id,
            scheduleId: updatePayload.scheduleId,
          }
        )

        await updateRegistration(updatePayload)

        console.log(
          `✅ updateRegistrationWithFormId: Successfully updated registration with ${formType} form ID: ${formId}`
        )
      } catch (error) {
        console.error(
          `❌ updateRegistrationWithFormId: Failed to update registration with ${formType} form ID:`,
          {
            error,
            formType,
            formId,
            scheduleId,
            isUpdated,
          }
        )
        // Don't throw error here to avoid breaking the form creation flow
      }
    },
    [isUpdated, updateRegistration, scheduleId, state]
  )

  const createPlayerForm = useCallback(async () => {
    console.log('🎯 createPlayerForm: Starting player form creation process', {
      scheduleId,
      existingPlayerForm: state.player_form,
      alreadyCreated: alreadyCreatedPlayerForm.current,
      isUpdated,
    })

    if (state.player_form) {
      console.log(
        '✅ createPlayerForm: Player form already exists in state',
        state.player_form
      )
      return state.player_form
    }

    if (alreadyCreatedPlayerForm.current) {
      console.log(
        '⚠️ createPlayerForm: Player form already created (ref flag is true), returning existing form',
        state.player_form
      )
      return state.player_form
    }

    try {
      console.log('🔄 createPlayerForm: Creating new player form', {
        name: `${scheduleId}_player_form`,
        id: `${scheduleId}_player_form`,
      })

      const newForm = await createForm({
        name: `${scheduleId}_player_form`,
        id: `${scheduleId}_player_form`,
      })

      console.log('✅ createPlayerForm: Form created successfully', newForm)

      alreadyCreatedPlayerForm.current = true
      console.log(
        '🏁 createPlayerForm: Set alreadyCreatedPlayerForm flag to true'
      )

      // Update state with the new form
      setState((prev) => {
        console.log(
          '🔄 createPlayerForm: Updating state with new player form',
          {
            previousPlayerForm: prev.player_form,
            newPlayerForm: newForm,
          }
        )
        return { ...prev, player_form: newForm }
      })

      // Immediately update the registration with the new form ID
      if (newForm?.id) {
        console.log(
          '🔄 createPlayerForm: Attempting to update registration with new form ID',
          {
            formId: newForm.id,
            isUpdated,
          }
        )
        await updateRegistrationWithFormId('player', newForm.id)
        console.log(
          '✅ createPlayerForm: Registration updated with player form ID'
        )
      } else {
        console.warn(
          '⚠️ createPlayerForm: New form has no ID, skipping registration update',
          newForm
        )
      }

      console.log(
        '🎉 createPlayerForm: Player form creation process completed successfully',
        newForm
      )
      return newForm
    } catch (error) {
      console.error('❌ createPlayerForm: Error during player form creation', {
        error,
        scheduleId,
        isUpdated,
      })
      throw error
    }
  }, [
    state.player_form,
    createForm,
    scheduleId,
    setState,
    updateRegistrationWithFormId,
    isUpdated,
  ])

  const createTeamForm = useCallback(async () => {
    console.log('🎯 createTeamForm: Starting team form creation process', {
      scheduleId,
      existingTeamForm: state.team_form,
      alreadyCreated: alreadyCreatedTeamForm.current,
      isUpdated,
    })

    if (alreadyCreatedTeamForm.current) {
      console.log(
        '⚠️ createTeamForm: Team form already created (ref flag is true), returning existing form',
        state.team_form
      )
      return state.team_form
    }

    if (state.team_form) {
      console.log(
        '✅ createTeamForm: Team form already exists in state',
        state.team_form
      )
      return state.team_form
    }

    try {
      console.log('🔄 createTeamForm: Creating new team form', {
        name: `${scheduleId}_team_form`,
        id: `${scheduleId}_team_form`,
      })

      const newForm = await createForm({
        name: `${scheduleId}_team_form`,
        id: `${scheduleId}_team_form`,
      })

      console.log('✅ createTeamForm: Form created successfully', newForm)

      alreadyCreatedTeamForm.current = true
      console.log('🏁 createTeamForm: Set alreadyCreatedTeamForm flag to true')

      // Update state with the new form
      setState((prev) => {
        console.log('🔄 createTeamForm: Updating state with new team form', {
          previousTeamForm: prev.team_form,
          newTeamForm: newForm,
        })
        return { ...prev, team_form: newForm }
      })

      // Immediately update the registration with the new form ID
      if (newForm?.id) {
        console.log(
          '🔄 createTeamForm: Attempting to update registration with new form ID',
          {
            formId: newForm.id,
            isUpdated,
          }
        )
        await updateRegistrationWithFormId('team', newForm.id)
        console.log('✅ createTeamForm: Registration updated with team form ID')
      } else {
        console.warn(
          '⚠️ createTeamForm: New form has no ID, skipping registration update',
          newForm
        )
      }

      console.log(
        '🎉 createTeamForm: Team form creation process completed successfully',
        newForm
      )
      return newForm
    } catch (error) {
      console.error('❌ createTeamForm: Error during team form creation', {
        error,
        scheduleId,
        isUpdated,
      })
      throw error
    }
  }, [
    state.team_form,
    createForm,
    scheduleId,
    setState,
    updateRegistrationWithFormId,
    isUpdated,
  ])

  const setCustomErroByField = (field: string, message: string) => {
    setState((prev) => ({ ...prev, fieldErrors: [...prev.fieldErrors, field] }))
    setState((prev) => ({
      ...prev,
      customError: { ...prev.customError, [field]: message },
    }))
  }

  const clearCustomErrors = () => {
    setState((prev) => ({ ...prev, fieldErrors: [], customError: {} }))
  }

  const setCustomErrors = (invalid_fields: any) => {
    if (invalid_fields.waivers) {
      setCustomErroByField('waivers', invalid_fields.waivers)
    }
    if (invalid_fields.name || invalid_fields.name_full) {
      setCustomErroByField('name', invalid_fields.name)
    }
    if (invalid_fields.currency) {
      setCustomErroByField('currency', invalid_fields.currency)
    }
    if (invalid_fields.payment_currency) {
      setCustomErroByField('payment_currency', invalid_fields.payment_currency)
    }
    if (invalid_fields.payment_type) {
      setCustomErroByField('payment_type', invalid_fields.payment_type)
    }
  }

  const submitRegistration = async () => {
    try {
      setState((prev) => ({
        ...prev,
        fieldErrors: [],
        customError: '',
        loading: true,
      }))

      if (state.show_waiver && state.waivers.length === 0) {
        console.log('🔄 submitRegistration: Showing waiver error')
        const error = new Error(
          'Please select at least one waiver'
        ) as Error & {
          invalid_fields?: Record<string, string>
        }
        error.invalid_fields = { waivers: 'Please select at least one waiver' }
        setActiveTab('2')
        throw error
      }

      // Handle registration data
      if (isUpdated) {
        await updateRegistration({
          scheduleId: scheduleId,
          allowPlayerRegistration: state.enablePlayerRegistration,
          registrationType:
            state.enableTeamRegistration && !state.enablePlayerRegistration
              ? 'team'
              : 'player',
          enableRegistration: true,
          eventNumber: '',
          currency: Number(state.currency),
          costPerRegistration: 0,
          paymentCurrency: Number(state.currency),
          paymentMethod: Number(state.payment_type),
          goLive: state.goLive,
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id: state?.player_form?.id || null,
          team_form_id: state?.team_form?.id || null,
          header_content: state.headerContent || null,
        })
      } else {
        await createRegistration({
          scheduleId: scheduleId,
          allowPlayerRegistration: state.registrationType === 'player',
          registrationType: state.registrationType,
          enableRegistration: true,
          eventNumber: '',
          currency: Number(state.currency),
          costPerRegistration: 0,
          paymentCurrency: Number(state.currency),
          paymentMethod: Number(state.payment_type),
          goLive: state.goLive,
          dateRange: {
            starts_at: state.competitionDateRange.starts_at || '',
            ends_at: state.competitionDateRange.ends_at || '',
          },
          useDarkTheme: state.darkMode,
          maxTeamSlots: 0,
          defaultTaxes: state.taxes,
          bccEmails: state.bccEmails,
          paymentProvider: state.payment_provider
            ? Number(state.payment_provider)
            : null,
          autoApproveRegistrations: state.autoApproveRegistrations,
          enableTeamRegistration: state.enableTeamRegistration,
          enablePlayerRegistration: state.enablePlayerRegistration,
          addPlayersOutsideOfRegistration:
            state.addPlayersOutsideOfRegistration,
          show_waiver: state?.show_waiver === true ? true : false,
          waivers: state?.waivers || [],
          player_form_id: state?.player_form?.id || null,
          team_form_id: state?.team_form?.id || null,
          header_content: state.headerContent || null,
        })
      }

      // Handle image upload/deletion
      if (state.customBackgroundImageChanged) {
        if (state.customBackgroundImage !== '') {
          await uploadRegistrationImage(scheduleId, state.customBackgroundImage)
        } else {
          await deleteRegistrationImage(scheduleId)
        }
      }
    } catch (error: any) {
      if (error?.invalid_fields) {
        setCustomErrors(error?.invalid_fields)
        throw error
      }
      throw error instanceof Error
        ? error
        : new Error('Failed to create registration')
    } finally {
      setState((prev) => ({ ...prev, loading: false }))
    }
  }

  const value: RegistrationSetupContextType = {
    state,
    setState,
    registrationTypes,
    availableTaxes,
    isUpdated,
    submitRegistration,
    apiWaivers,
    refetchWaivers,
    apiAccount,
    registrationId: registration?.id || null,
    registration,
    setCustomErroByField,
    clearCustomErrors,
    createPlayerForm,
    createTeamForm,
    scheduleId,
    activeTab,
    setActiveTab,
  }

  useEffect(() => {
    if (registration) {
      const paymentProviderId = isUpdated
        ? registration?.payment_provider_type?.id
        : apiAccount?.payment_provider_type?.id || null
      let currency = registration?.currency?.id || '2'
      let playerForm = registration?.player_form
      let teamForm = registration?.team_form
      setState((prevState) => ({
        ...prevState,
        competitionDateRange: {
          starts_at:
            registration?.starts_at || dayjs().utc().format('YYYY-MM-DD'),
          ends_at: registration?.ends_at || dayjs().utc().format('YYYY-MM-DD'),
        },
        currency: currency,
        payment_provider: paymentProviderId,
        taxes: registration?.taxes?.map((tax) => tax.id) || [],
        bccEmails: registration?.bcc_registrations_to?.split(',') || [],
        goLive: registration?.is_live,
        registrationType:
          registration?.registration_type_id === 1 ? 'team' : 'player',
        paidBy: 'team',
        darkMode: registration?.dark_theme,
        customBackgroundImage: registration?.image?.full_path || '',
        customBackgroundImageChanged: false,
        enableTeamRegistration: registration?.is_team_registration,
        enablePlayerRegistration: registration?.is_player_registration,
        autoApproveRegistrations: registration?.auto_approve,
        show_waiver: registration?.show_waiver,
        waivers: registration?.waivers?.map((waiver) => waiver.id) || [],
        addPlayersOutsideOfRegistration:
          registration?.can_add_players_from_roster,
        player_form: playerForm,
        team_form: teamForm,
        headerContent: registration?.header_content || '',
        activeTab: activeTab,
      }))
    }
  }, [registration, isUpdated, apiAccount])

  return (
    <RegistrationSetupContext.Provider value={value}>
      {children}
    </RegistrationSetupContext.Provider>
  )
}

export { RegistrationSetupContextWrapper }
